import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageCode } from "./utils/translations";
import AutomateBusiness from "./page/AutomateBusiness";
import TemplateLibraryWrapper from "./features/templates/TemplateLibraryPage";
import CreateTemplatePage from "./features/templates/CreateTemplatePage";

import MainLayout from "./MainLayout";
import HomePage from "./features/home/HomePage";
import PrivacyPolicy from "./sections/PrivacyPolicy";
import YouTubeInsights from "./page/youtube";
import TermsOfService from "./page/TermsOfService";
import BlogDashboard from "./page/blogs/BlogDashboard";
import ManageSocialMediaDashboard from "./page/blogs/ManageSocialMediaDashboard";
import AutomationTools from "./page/blogs/AutomationTools";
import GrowBusinessOnline from "./page/blogs/GrowBusinessOnline";

import AboutPage from "./page/AboutPage";
import Pricing from "./page/pricing";
import ChatWidget from "./component/chatwidget";
import IntegrationsPanel from "./component/IntegrationsPanel";

import RedirectToDefaultApp from "./component/RedirectToDefaultApp";
import UserProfile from "./page/UserProfile";
import Dashboard from "./page/DashboardAdvanced";
import PointsHistory from "./page/PointsHistory";
import Social from "./page/Social";
import FieldServicePlanner from "./page/FieldServicePlanner";
import { fetchGeoData, GeoData } from "./services/locationService";
import { MobileAppsPanel } from "./features/mobile/MobileAppsPanel";
import { WebsitePanel } from "./features/website/WebsitePanel";
import EmailValidator from "./sections/EmailValidator";
import ServicesIndex from "./features/services/ServicesIndex";
import ServicePage from "./features/services/ServicePage";
import { SERVICES } from "./features/services/servicesData";

import YouTubeConnectPage from "./page/YouTubeConnectPage";
import FacebookConnectPage from "./page/FacebookConnectPage";
import YouTubeAnalyticsPanel from "./page/YouTubeAnalyticsPanel";
import YouTubeWorldClassDashboard from "./page/YouTubeWorldClassDashboard";

import MultipleInstagramAccounts from "./page/footer/MultipleInstagramAccounts";
import MultipleFacebookAccounts from "./page/footer/MultipleFacebookAccounts";
import SocialMediaAutomation from "./page/footer/SocialMediaAutomation";
import SMSMarketing from "./page/footer/SMSMarketing";
import EmailMarketing from "./page/footer/EmailMarketing";
import BulkEmailCampaigns from "./page/footer/BulkEmailCampaigns";
import MultiAccountManager from "./page/footer/MultiAccountManager";
import FestiveOfferPopup from "./component/FestiveOfferPopup";

const App: React.FC = () => {
  const [lang, setLang] = useState<LanguageCode>("en");
  const [geo, setGeo] = useState<GeoData | null>(null);

  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent StrictMode double-call in development
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Log once per browser session
    const alreadyLogged = sessionStorage.getItem("geoLogged");
    if (alreadyLogged) return;

    const loadGeo = async () => {
      const data = await fetchGeoData();
      console.log("🌍 GEO DATA:", data);

      if (data) {
        setGeo(data);
        sessionStorage.setItem("geoLogged", "true");
      }
    };

    loadGeo();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/dashboard"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              {" "}
              <Dashboard lang={lang} />
            </MainLayout>
          }
        >
          <Route path="integrations" element={<IntegrationsPanel />} />
          <Route path="youtubeConnect" element={<YouTubeConnectPage />} />
          <Route path="facebookConnect" element={<FacebookConnectPage />} />
          <Route
            path="views"
            element={<YouTubeAnalyticsPanel mode="views" />}
          />
          <Route
            path="engagement"
            element={<YouTubeAnalyticsPanel mode="engagement" />}
          />
          <Route path="commander" element={<YouTubeWorldClassDashboard />} />
          <Route
            path="mobile/all"
            element={<MobileAppsPanel section="allApps" />}
          />
          <Route
            path="website/all"
            element={<WebsitePanel section="allWebsites" />}
          />

          <Route
            path="mobile/analytics/:appSlug"
            element={<MobileAppsPanel section="appAnalytics" />}
          />
          <Route
            path="automation/:type/templates"
            element={<TemplateLibraryWrapper />}
          />
          <Route
            path="automation/:type/templates/create"
            element={<CreateTemplatePage />}
          />
        </Route>
        <Route
          path="/userprofile"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <UserProfile lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/points"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <PointsHistory lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/products/field-service-planner"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <FieldServicePlanner lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/automate-business"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <AutomateBusiness lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/social"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <Social lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/pricing"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <Pricing geo={geo} lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/services"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <ServicesIndex lang={lang} />
            </MainLayout>
          }
        />

        {SERVICES.map((service) => (
          <Route
            key={service.slug}
            path={`/services/${service.slug}`}
            element={
              <MainLayout lang={lang} setLang={setLang}>
                <ServicePage slug={service.slug} lang={lang} />
              </MainLayout>
            }
          />
        ))}

        <Route
          path="/"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <HomePage lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/about"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <AboutPage lang={lang} />
            </MainLayout>
          }
        />
        <Route
          path="/multiple-instagram-accounts"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <MultipleInstagramAccounts lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/multiple-facebook-accounts"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <MultipleFacebookAccounts lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/social-media-automation"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <SocialMediaAutomation lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/sms-marketing"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <SMSMarketing lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/email-marketing"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <EmailMarketing lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/bulk-email-campaigns"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <BulkEmailCampaigns lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/multi-account-manager"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <MultiAccountManager lang={lang} />
            </MainLayout>
          }
        />
        <Route
          path="/blogs"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <BlogDashboard lang={lang} />
            </MainLayout>
          }
        />
        <Route
          path="/blogs/manage-social-media-dashboard"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <ManageSocialMediaDashboard />
            </MainLayout>
          }
        />
        <Route
          path="/blogs/automation-tools-business"
          element={<AutomationTools />}
        />
        <Route
          path="/blogs/grow-business-online"
          element={<GrowBusinessOnline />}
        />
        <Route
          path="/youtube"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <YouTubeInsights lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/terms-of-service"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <TermsOfService lang={lang} />
            </MainLayout>
          }
        />

        <Route
          path="/privacy-policy"
          element={
            <MainLayout lang={lang} setLang={setLang}>
              <PrivacyPolicy />
            </MainLayout>
          }
        />
      </Routes>

      <FestiveOfferPopup />
      <ChatWidget />
    </BrowserRouter>
  );
};

export default App;
